import { useEffect, useMemo, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { LogOut, Menu } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { canRoleAccessPath, getRoleDisplayLabel, getRouteById, getRouteMeta, getVisibleSections, normalizeRole } from "@/lib/portalModules";
import { BrandLockup } from "@/components/brand/BrandLockup";
import { ActionMenu } from "@/components/portal/ActionMenu";
import { labelize } from "@/lib/portal";

const SectionNav = ({ sections, onNavigate }) => (
  <div className="sidebar-sections">
    {sections.map((section) => (
      <section className="sidebar-section" key={section.id}>
        <p className="sidebar-section-label">{section.label}</p>
        <div className="sidebar-section-items">
          {section.items.map((item) => {
            if (item.navKind === "group") {
              const GroupIcon = item.icon;
              return (
                <div className="sidebar-group" key={item.id}>
                  <div className="sidebar-group-label">
                    <GroupIcon size={16} />
                    <span>{item.label}</span>
                  </div>
                  <div className="sidebar-child-links">
                    {item.children.map((child) => {
                      const ChildIcon = child.icon;
                      return (
                        <NavLink
                          className={({ isActive }) => (isActive ? "sidebar-child-link active" : "sidebar-child-link")}
                          data-testid={child.testId || `sidebar-nav-${child.id}-link`}
                          key={child.id}
                          onClick={onNavigate}
                          to={child.path}
                        >
                          <ChildIcon size={15} />
                          <span>{child.label}</span>
                          <Badge className={child.parityState === "working" ? "vlq-badge tone-approved" : "vlq-badge tone-muted"}>
                            {child.parityState === "working" ? "Live" : "Restore"}
                          </Badge>
                        </NavLink>
                      );
                    })}
                  </div>
                </div>
              );
            }

            const ParentIcon = item.icon;
            return (
              <div className="sidebar-group" key={item.id}>
                <NavLink
                  className={({ isActive }) => (isActive ? "sidebar-link active" : "sidebar-link")}
                  data-testid={item.testId || `sidebar-nav-${item.id}-link`}
                  onClick={onNavigate}
                  to={item.path}
                >
                  <ParentIcon size={18} />
                  <span>{item.label}</span>
                  <Badge className={item.parityState === "working" ? "vlq-badge tone-approved" : "vlq-badge tone-muted"}>
                    {item.parityState === "working" ? "Live" : "Restore"}
                  </Badge>
                </NavLink>
                {item.children?.length ? (
                  <div className="sidebar-child-links">
                    {item.children.map((child) => {
                      const ChildIcon = child.icon;
                      return (
                        <NavLink
                          className={({ isActive }) => (isActive ? "sidebar-child-link active" : "sidebar-child-link")}
                          data-testid={child.testId || `sidebar-nav-${child.id}-link`}
                          key={child.id}
                          onClick={onNavigate}
                          to={child.path}
                        >
                          <ChildIcon size={15} />
                          <span>{child.label}</span>
                        </NavLink>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </section>
    ))}
  </div>
);

export const PortalShell = ({ installUi, onLogout, user }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const normalizedRole = normalizeRole(user?.role);

  const visibleSections = useMemo(() => getVisibleSections(normalizedRole), [normalizedRole]);
  const routeMeta = useMemo(() => getRouteMeta(location.pathname), [location.pathname]);
  const quickActions = routeMeta.quickActions || [];
  const isAdminOverviewTopbar = routeMeta.path === "/overview" && normalizedRole === "admin";
  const compactMobileTopbar = isAdminOverviewTopbar || ["/rm/mobile", "/supply/connections"].includes(routeMeta.path);
  const topbarTitle = isAdminOverviewTopbar ? "Admin Command" : routeMeta.title;
  const topbarSubtitle = isAdminOverviewTopbar ? "Open the right workspace quickly." : routeMeta.subtitle;
  const relatedRoutes = useMemo(
    () => (routeMeta.relatedRoutes || [])
      .map((routeId) => getRouteById(routeId))
      .filter(Boolean)
      .filter((route) => canRoleAccessPath(user?.role, route.path))
      .filter((route) => route.path !== routeMeta.path),
    [routeMeta, user?.role],
  );

  useEffect(() => {
    document.title = `${topbarTitle} | VenuLoQ Team Portal`;
  }, [topbarTitle]);

  useEffect(() => {
    setMobileNavOpen(false);
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [location.pathname]);

  const handleQuickAction = (action) => {
    if (action.kind === "chat") {
      navigate(`/chat/shared?source=${routeMeta.id}`);
      return;
    }
    if (action.kind === "refresh") {
      window.location.reload();
      return;
    }
    if (action.kind === "route" && action.to) {
      navigate(action.to);
    }
  };

  return (
    <div className="portal-layout">
      {mobileNavOpen ? (
        <div className="mobile-nav-overlay" data-testid="mobile-nav-overlay">
          <div className="mobile-nav-panel">
            <div className="mobile-nav-header">
              <strong>Navigation</strong>
              <button className="mobile-nav-close-plain" data-testid="mobile-nav-close-button" onClick={() => setMobileNavOpen(false)} type="button">
                Close
              </button>
            </div>
            <div className="sheet-nav-wrap">
              <SectionNav onNavigate={() => setMobileNavOpen(false)} sections={visibleSections} />
            </div>
          </div>
        </div>
      ) : null}
      <aside className="portal-sidebar desktop-sidebar">
        <div className="sidebar-brand-block">
          <BrandLockup className="sidebar-brand-lockup" compact descriptor="TEAM PORTAL" onClick={() => navigate('/overview')} testId="sidebar-brand-lockup" variant="dark" />
        </div>

        <SectionNav onNavigate={() => setMobileNavOpen(false)} sections={visibleSections} />

        <Card className="surface-card sidebar-status-card">
          <CardHeader>
            <CardTitle>Connected operating engine</CardTitle>
            <CardDescription>Navigation parity is being restored module by module without breaking live workflows.</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="card-copy">
              Current role surface: <strong>{labelize(normalizedRole)}</strong>. Shared Team Chat, Finance, Payroll, and HR remain live while additional operations modules are restored.
            </p>
          </CardContent>
        </Card>
      </aside>

      <main className="portal-main">
        <header className={compactMobileTopbar ? "portal-topbar compact-mobile-topbar" : "portal-topbar"}>
          <div className="topbar-left">
            <div className="mobile-only mobile-nav-wrap">
              <button aria-label="Open navigation" className="mobile-nav-plain-button" data-testid="mobile-nav-toggle-button" onClick={() => setMobileNavOpen(true)} type="button">
                <span className="mobile-nav-plain-icon"><Menu size={18} /></span>
                <span>Menu</span>
              </button>
            </div>
            <div>
              <p className="eyebrow">VenuLoQ internal operating engine</p>
              <h1 className="page-title" data-testid="topbar-page-title">{topbarTitle}</h1>
              <p className="page-subtitle">{topbarSubtitle}</p>
            </div>
          </div>

          <div className="topbar-right" data-testid="topbar-quick-actions">
            {relatedRoutes.length ? (
              <ActionMenu
                dataTestId="topbar-related-pages-menu"
                items={relatedRoutes.map((route) => ({
                  label: route.label,
                  onSelect: () => navigate(route.path),
                  testId: `topbar-related-${route.id}-item`,
                }))}
                triggerLabel="Related pages"
              />
            ) : null}
            {quickActions.length ? (
              <ActionMenu
                dataTestId="topbar-page-actions-menu"
                items={quickActions.map((action) => ({
                  label: action.label,
                  disabled: action.kind === "restoration",
                  onSelect: () => handleQuickAction(action),
                  testId: `topbar-action-${action.id}-item`,
                }))}
                triggerLabel="Page actions"
              />
            ) : null}
            {installUi?.canInstall ? (
              <Button className="secondary-button compact-button" data-testid="topbar-install-app-button" onClick={installUi.promptInstall}>
                Install app
              </Button>
            ) : installUi?.installed ? (
              <Badge className="vlq-badge tone-approved" data-testid="topbar-installed-app-badge">Installed</Badge>
            ) : null}
            <div className="topbar-meta-pills" data-testid="topbar-role-team-meta">
              <Badge className="vlq-badge tone-gold">{getRoleDisplayLabel(normalizedRole)}</Badge>
              <Badge className="vlq-badge tone-muted">{user.team}</Badge>
            </div>
            <Button className="icon-and-text-button" data-testid="topbar-logout-button" onClick={onLogout} variant="outline">
              <LogOut size={16} />
              Logout
            </Button>
          </div>
        </header>

        <div className="content-wrap">
          <Outlet />
        </div>
      </main>
    </div>
  );
};
